import { AddressData } from 'newTypes/location'
import { Unit, UnitAddress } from 'newTypes/unit'
import { VerificationStatus } from 'types/property'
import { ApplicationPipelineStatus } from 'types/workflowStatus'

export type ListingStatusVariant = 'active' | 'verification' | 'paused' | 'closed'

export function toAddressData(address: unknown): AddressData | null {
  if (!address) return null
  if (typeof address === 'string') {
    const trimmed = address.trim()
    return trimmed ? { formatted_address: trimmed } : null
  }
  if (typeof address === 'object') return address as AddressData
  return null
}

export function hasMeaningfulAddress(address: unknown): boolean {
  const data = toAddressData(address)
  if (!data) return false
  return Boolean(
    data.formatted_address?.trim() ||
      data.street?.trim() ||
      (data.city?.trim() && data.province?.trim())
  )
}

export function propertyAddressToAddressData(
  address: UnitAddress | null | undefined
): AddressData | null {
  if (!address) return null

  const record = address as UnitAddress & { country?: string; zip_code?: string }
  const zipCode = record.zipCode ?? record.zip_code
  const parts = [record.number, record.street, record.city, record.province, zipCode].filter(
    Boolean
  )

  return {
    city: record.city,
    country: record.country,
    province: record.province,
    street: record.street,
    number: record.number,
    door_number: record.doorNumber,
    zip_code: zipCode,
    formatted_address: parts.join(', '),
    location: record.location,
    geometry: record.geometry as AddressData['geometry'],
  }
}

export function getFirstRegisteredProperty(listings: Unit[] | undefined): Unit | undefined {
  if (!listings?.length) return undefined

  return [...listings].reduce((oldest, current) => {
    const oldestTime = new Date(oldest.createdAt || 0).getTime()
    const currentTime = new Date(current.createdAt || 0).getTime()
    if (
      Number.isFinite(currentTime) &&
      Number.isFinite(oldestTime) &&
      currentTime !== oldestTime
    ) {
      return currentTime < oldestTime ? current : oldest
    }
    const oldestId = Number(oldest.id) || 0
    const currentId = Number(current.id) || 0
    return currentId < oldestId ? current : oldest
  })
}

export function resolveProfileAddress(
  savedAddress: unknown,
  sessionAddress: unknown,
  firstPropertyAddress: AddressData | null
): AddressData | null {
  if (hasMeaningfulAddress(sessionAddress)) {
    return toAddressData(sessionAddress)
  }
  if (hasMeaningfulAddress(savedAddress)) {
    return toAddressData(savedAddress)
  }
  return firstPropertyAddress
}

/** Full address line (tooltip, title). */
export function formatFullUserAddress(address: unknown): string {
  if (!address) return ''
  if (typeof address === 'string') return address.trim()

  const data = toAddressData(address)
  if (!data) return ''

  if (data.formatted_address?.trim()) {
    return data.formatted_address.trim()
  }

  const neighborhood = (data as AddressData & { neighborhood?: string }).neighborhood
  const parts = [
    data.number,
    data.street,
    neighborhood,
    data.city,
    data.province,
    data.zip_code,
    data.country,
  ]
    .filter((part): part is string => typeof part === 'string' && part.trim() !== '')
    .map(part => part.trim())
  return parts.join(', ')
}

export function canSubmitListingVerification(
  verified: VerificationStatus | boolean | null | undefined
): boolean {
  if (!verified) return true
  if (verified === true) return false
  return verified === VerificationStatus.Rejected
}

export function isListingAwaitingVerification(
  verified: VerificationStatus | boolean | null | undefined
): boolean {
  return verified === VerificationStatus.Pending
}

export function getListingStatusKey(listing: Unit): ListingStatusVariant {
  const meta = listing.listingMeta as { status?: string } | null | undefined
  const metaStatus = typeof meta?.status === 'string' ? meta.status : null

  // Explicit lifecycle statuses win over verified flags (paused/closed stay paused/closed).
  if (metaStatus === 'paused') return 'paused'
  if (metaStatus === 'closed' || metaStatus === 'rejected') return 'closed'
  if (
    metaStatus === 'property_validation_pending' ||
    metaStatus === 'property_docs_in_review' ||
    metaStatus === 'property_correction_requested' ||
    metaStatus === 'ready_to_publish' ||
    metaStatus === 'draft'
  ) {
    return 'verification'
  }
  if (listing.verified === VerificationStatus.Pending) {
    return 'verification'
  }
  if (listing.verified === VerificationStatus.Rejected) {
    return 'closed'
  }
  if (metaStatus === 'active' || listing.verified === VerificationStatus.Approved) {
    return 'active'
  }
  // Missing meta: infer carefully — never treat unknown as live.
  if (!metaStatus) {
    if (listing.verified === VerificationStatus.Approved) return 'active'
    return 'verification'
  }
  return 'active'
}

const STATUS_BADGE_BASE =
  'inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide shadow-sm'

export function listingStatusBadgeClass(variant: ListingStatusVariant): string {
  if (variant === 'active') {
    return `${STATUS_BADGE_BASE} border-emerald-400 bg-emerald-100 text-emerald-900`
  }
  if (variant === 'verification') {
    return `${STATUS_BADGE_BASE} border-blue-300 bg-blue-50 text-imovSecondary`
  }
  if (variant === 'paused') {
    return `${STATUS_BADGE_BASE} border-slate-300 bg-slate-100 text-slate-600`
  }
  return `${STATUS_BADGE_BASE} border-gray-300 bg-gray-100 text-gray-700`
}

const LISTING_ACTION_WIDTH =
  'inline-flex w-full min-w-[11rem] max-w-full items-center justify-center text-center sm:min-w-[12rem]'

/** Pill shown while ownership docs are under review. */
export const awaitingVerificationClass = `inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-xs font-semibold text-imovSecondary shadow-sm ${LISTING_ACTION_WIDTH}`

export const verifyListingButtonClass = `${LISTING_ACTION_WIDTH} rounded-lg border-2 border-imovPrimary/30 bg-white px-5 py-2.5 text-sm font-semibold text-imovPrimary shadow-sm transition-colors hover:border-imovPrimary/50 hover:bg-blue-50`

export const manageListingButtonClass = `${LISTING_ACTION_WIDTH} rounded-lg bg-imovPrimary px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-colors hover:bg-imovPrimaryDark`

export const favoritesCountBadgeClass = `${STATUS_BADGE_BASE} border-imovPrimary/50 bg-blue-100 text-imovSecondary`

export function applicationStatusBadgeClass(
  status: ApplicationPipelineStatus
): string {
  if (status === 'approved') {
    return `${STATUS_BADGE_BASE} border-emerald-400 bg-emerald-100 text-emerald-900`
  }
  if (status === 'rejected' || status === 'withdrawn') {
    return `${STATUS_BADGE_BASE} border-red-400 bg-red-100 text-red-900`
  }
  return `${STATUS_BADGE_BASE} border-blue-300 bg-blue-50 text-imovSecondary`
}

export function formatListingAddress(listing: Unit): string {
  const addr = listing.address
  if (!addr) return ''
  const parts = [addr.street, addr.city, addr.province].filter(Boolean)
  return parts.join(', ')
}

export function applicationStatusProgress(status: string | undefined): number {
  switch (applicationStatusKey(status)) {
    case 'approved':
    case 'rejected':
    case 'withdrawn':
      return 100
    case 'documents_requested':
    case 'documents_in_review':
    case 'correction_requested':
      return 75
    case 'owner_review':
    case 'submitted_to_owner':
      return 50
    case 'identity_required':
      return 35
    default:
      return 25
  }
}

export function applicationStatusKey(status: string | undefined): ApplicationPipelineStatus {
  const legacyMap: Record<string, ApplicationPipelineStatus> = {
    new: 'submitted_to_owner',
    in_review: 'owner_review',
    docs_requested: 'documents_requested',
  }
  const value = status || 'started'
  if (legacyMap[value]) return legacyMap[value]
  if (
    value === 'started' ||
    value === 'identity_required' ||
    value === 'documents_requested' ||
    value === 'documents_in_review' ||
    value === 'correction_requested' ||
    value === 'submitted_to_owner' ||
    value === 'owner_review' ||
    value === 'approved' ||
    value === 'rejected' ||
    value === 'withdrawn'
  ) {
    return value
  }
  return 'started'
}
